import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Button, Box, Grid, CardMedia } from '@mui/material';
import ResponsiveAppBar from '../components/AppBar';
import Footer from '../components/Footer.js';
import '../CardAnimations.css';
import { keyframes } from '@mui/system';

const slideIn = keyframes`
0% { transform: translateX(-100%); opacity: 0; }
100% { transform: translateX(0); opacity: 1; }
`;

const colors = {
    white: "#FFFFFF",
    orange: "#FF6A1A",
    brown: "#532912",
    gray: "#7A7979",
    orange2: "#AA4D1B",
    darkbrown: "#29180E",
    black: "#000000",
};

const titleItemStyles = {
    color: colors.white,
    fontWeight: 600,
};

const fadeIn = {
    '@keyframes fadeIn': {
        '0%': { opacity: 0 },
        '100%': { opacity: 1 },
    },
};

export default function Home() {
    const navigate = useNavigate();

    const handleKontaktClick = () => navigate('/kontakt');


    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <ResponsiveAppBar />
            <div style={{ flex: '1 0 auto' }}>
                {/* Background Image Box with Overlay */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        minHeight: { xs: '100vh', md: '100vh' },
                        backgroundImage: `url('/assets/Homesite.jpg')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        '::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1,
                        },
                    }}
                />

                {/* Content Box for Logo and Text */}
                <Box
                    sx={{
                        ...fadeIn,
                        animation: 'fadeIn 2s ease-out',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingTop: { xs: '0px', sm: '25px' },
                        zIndex: 2,  // Ensures content stays above overlay
                    }}
                >
                    {/* Logo Image */}
                    <Box
                        component="img"
                        src="/assets/logo sa slogan2.png"
                        alt="Logo and Slogan"
                        sx={{
                            animation: `${slideIn} 1s ease-out`,
                            width: { xs: '100%', sm: '85%', md: '70%', lg: '50%' }, // Adjust size based on screen size
                            marginBottom: 0,
                        }}
                    />

                    {/* Centered Content Container */}
                    <Container maxWidth="sm" sx={{ textAlign: 'center', zIndex: 2 }}>
                        {/* Welcome Text */}
                        {/* <Typography
                            variant="h5"
                            component="h1"
                            gutterBottom
                            sx={{ animation: `${slideIn} 1s ease-out`, ...titleItemStyles, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 0 }}
                        >
                            Willkommen bei Unger Bau
                        </Typography> */}
                        {/* <Typography
                            variant="h6"
                            color="textSecondary"
                            sx={{ animation: `${slideIn} 1s ease-out`, ...titleItemStyles, mb: 0 }}
                        >
                            Ihr zuverlässiger Partner für Bauen und Renovieren.
                        </Typography> */}

                        {/* Button */}
                        <Button
                            variant="contained"
                            sx={{
                                animation: `${slideIn} 1s ease-out`,
                                backgroundColor: colors.orange,
                                paddingX: 0.5,
                                paddingY: 0.5,
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                marginTop: 0,
                                width: 'auto',
                                transition: 'transform 0.3s',
                                '&:hover': { transform: 'scale(1.05)' },
                            }}
                            onClick={handleKontaktClick}
                        >
                            Schreiben Sie uns
                        </Button>
                    </Container>
                </Box>

                {/* Main Content Section */}
                <Box sx={{ padding: 3, maxWidth: { xs: "100%", sm: "1300px" }, margin: '0 auto', marginTop: 2, marginBottom: '16px', }}>
                    <Typography variant="h5" component="h1" gutterBottom sx={{ fontSize: { xs: '1.2rem', sm: '2rem' },fontWeight:'bold' }}>
                        Herzlich willkommen bei Unger Bau, Ihrem Meisterbetrieb für Maurer- und Stahlbetonbau
                    </Typography>
                </Box>

                {/* Leistungen Section */}
                <Container maxWidth="lg" sx={{ mb: 10 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <HoverCard
                                image="/assets/Beratung.jpg"
                                title="Planung"
                                text="Fragen? Gerne unterstützen wir Sie bei der Planung Ihres Bauprojekts – von der ersten Idee bis zur vollständigen Umsetzung."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <HoverCard
                                image="/assets/Maurerarbeiten.jpg"
                                title="Maurerarbeiten"
                                text="Tragende Wände, Zwischenwände oder Sicht- und Ziermauerwerk aus verschiedensten Steinarten. Bei uns erhalten Sie meisterhafte Qualität!"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <HoverCard
                                image="/assets/Stahlbetonbau.jpg"
                                title="Stahlbetonbau"
                                text="Von Schalarbeiten über komplexe Bewehrungsstrukturen zur Betonage. Wir garantieren präzise Ausführung und detailgetreue Umsetzungen ihrer architektonischen sowie statischen Anforderungen."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <HoverCard
                                image="/assets/Abbruch u. Umbau.jpg"
                                title="Abbruch u. Umbau"
                                text="Fachgerechte Demontage und Durchbrüche. Vertrauen Sie auf unsere Expertise, um Platz für Neues zu schaffen und bestehende Strukturen optimal anzupassen."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <HoverCard
                                image="/assets/Reparaturen u. Sanierung.jpg"
                                title="Reparaturen/Sanierung"
                                text="Wir revitalisieren und reparieren alte oder beschädigte Strukturen. Unsere Sanierungsdienste stellen die Integrität Ihres Eigentums wieder her und verbessern sowohl die Funktionalität als auch die Wohnqualität."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <HoverCard
                                image="/assets/Gartengestaltung.jpg"
                                title="Gartengestaltung"
                                text="Ob Sie eine idyllische Rückzugsoase oder einen praktischen und stilvollen Außenbereich wünschen – wir planen und realisieren Ihren Traumgarten, inklusive der Verlegung von Pflastersteinen und Terrassenplatten."
                            />
                        </Grid>
                    </Grid>
                </Container>

                {/* Footer Section */}
                <Footer />
            </div>
        </div>
    );
}

function HoverCard({ image, title, text }) {
    const [isHovered, setIsHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Check window size to determine if it's mobile
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768); // Assuming mobile is below 768px width
        };
        window.addEventListener('resize', handleResize);
        handleResize(); // Call initially to set state
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMouseEnter = () => {
        if (!isMobile) setIsHovered(true);
    };

    const handleMouseLeave = () => {
        if (!isMobile) setIsHovered(false);
    };
    return (
        <Box
            className="hover-card"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
                transition: 'transform 0.3s',
                '&:hover': {
                    transform: 'scale(1.05)', // Scale on hover
                },
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <CardMedia
                component="img"
                height={300}
                image={image}
                alt={title}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />

            {/* Title always visible (only for Desktop) */}
            {!isMobile && !isHovered && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 10,
                        left: 10,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: colors.white,
                        padding: '0px 5px',
                        borderRadius: '5px',
                    }}
                >
                    <Typography variant="h5" gutterBottom sx={{ margin: '0px', fontWeight: 'bold', color: colors.white }}>
                        {title}
                    </Typography>
                </Box>
            )}

            {/* Title and Text shown on hover */}
            {isHovered && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: colors.white,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '10px',
                    }}
                >
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: colors.white }}>
                        {title}
                    </Typography>
                    <Typography variant="body1" textAlign="center" px={2}>
                        {text}
                    </Typography>
                </Box>
            )}

            {/* Mobile Content */}
            {isMobile && !isHovered && (
                <Box sx={{ padding: 2, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: colors.black }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" textAlign="center">
                        {text}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
